/**
 * @fileoverview MissingTimesheetsNotice — "you still owe time" on My
 * Timesheets (MOR-1461).
 *
 * Shaw Gidley's admin staff chase people for timesheets by hand. This keeps
 * the debt in front of the person who owes it: how many days, how far short,
 * and a jump to the first one. Owners and admins can clear the notice and
 * the sidebar badge for days up to yesterday. Expected hours stay, so a
 * later short day shows again. Prominent, never blocking.
 */

import { format, parseISO } from "date-fns";
import { CalendarClock } from "lucide-react";

import { Button } from "@/ui/primitives";

export function MissingTimesheetsNotice({
  count,
  shortHours,
  firstDay,
  onGoToFirstDay,
  onClear,
}: {
  count: number;
  shortHours: number;
  firstDay: string | undefined;
  onGoToFirstDay: (day: string) => void;
  /**
   * Owners and admins can dismiss the current notice and sidebar badge.
   * Absent for everyone else.
   */
  onClear?: () => void;
}) {
  const hours = shortHours.toLocaleString("en-AU", {
    maximumFractionDigits: 1,
  });
  return (
    <div
      role="status"
      aria-label="Missing timesheets"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <CalendarClock className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="font-medium">
          {count === 1
            ? "1 day of timesheets missing"
            : `${count} days of timesheets missing`}
        </span>
        {firstDay && (
          <>
            {" "}
            since {format(parseISO(firstDay), "EEE d MMM")} ({hours}{" "}
            {shortHours === 1 ? "hour" : "hours"} in total).
          </>
        )}
      </span>
      {onClear && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 border-amber-300 bg-transparent text-xs text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/40"
          title="Clears this notice and the sidebar badge through yesterday. Time is still expected."
          aria-label="Clear missing timesheets notice"
          onClick={onClear}
        >
          Clear
        </Button>
      )}
      {firstDay && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 border-amber-300 bg-transparent text-xs text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/40"
          onClick={() => onGoToFirstDay(firstDay)}
        >
          Go to first day
        </Button>
      )}
    </div>
  );
}
